// Leaderboards (contracts/phase2.md §7, §12): pick a board, see the global top
// 100 with user ids for moderation. Players on a leaderboard hold or with
// showOnLeaderboards off are left out by the API, as on the public boards.

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useLeaderboard } from "../api/queries";
import { LEADERBOARDS, type Leaderboard, type LeaderboardName } from "../api/schemas";
import { tableStyles } from "../components/table";
import { Button, Chip, EmptyState, ErrorState, Field, PageHeader, Segmented, Skeleton, TextInput } from "../components/ui";
import { fmtInt } from "../lib/format";
import styles from "./pages.module.css";

const BOARD_LABEL: Record<LeaderboardName, string> = {
  daily: "Daily",
  hard: "Hard",
  "xp-week": "XP this week",
  "xp-all": "XP all time",
  streak: "Streak",
};
const VALUE_HEAD: Record<LeaderboardName, string> = { daily: "Score", hard: "Score", "xp-week": "XP", "xp-all": "XP", streak: "Streak" };

const hasN = (b: LeaderboardName) => b === "daily" || b === "hard";

function formatValue(board: LeaderboardName, v: number) {
  if (board === "streak") return `${fmtInt(v)}d`;
  if (board === "xp-week" || board === "xp-all") return `${fmtInt(v)} XP`;
  return fmtInt(v);
}

export function LeaderboardsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("board");
  const board: LeaderboardName = (LEADERBOARDS as readonly string[]).includes(raw ?? "") ? (raw as LeaderboardName) : "daily";
  const nParam = params.get("n");
  const n = hasN(board) && nParam && /^\d+$/.test(nParam) ? Number(nParam) : null;
  const q = useLeaderboard(board, n);
  const shownN = q.data?.board === board ? q.data.n : null;
  // The puzzle-number field follows the URL and the server's default (the latest puzzle).
  const base = n !== null ? String(n) : shownN !== null ? String(shownN) : "";
  const [draft, setDraft] = useState(base);
  const [seen, setSeen] = useState(base);
  if (seen !== base) {
    setSeen(base);
    setDraft(base);
  }

  const go = (next: { board?: LeaderboardName; n?: number | null }) => {
    const b = next.board ?? board;
    const p: Record<string, string> = {};
    if (b !== "daily") p.board = b;
    const nn = next.n === undefined ? n : next.n;
    if (hasN(b) && nn !== null && nn !== undefined) p.n = String(nn);
    setParams(p, { replace: true });
  };
  const current = n ?? shownN;

  return (
    <>
      <PageHeader
        title="Leaderboards"
        meta={
          <span>
            The top 100 with user ids for moderation. Players the global board hides (opted out, or held by moderation) are listed and
            flagged; players see the board without them.
          </span>
        }
      />
      <div className={styles.filters}>
        <Segmented
          label="Board"
          value={board}
          onChange={(b) => go({ board: b, n: hasN(b) ? n : null })}
          options={LEADERBOARDS.map((b) => ({ value: b, label: BOARD_LABEL[b] }))}
        />
        {hasN(board) ? (
          <form
            className={styles.nPicker}
            onSubmit={(e) => {
              e.preventDefault();
              go({ n: /^\d+$/.test(draft) && Number(draft) > 0 ? Number(draft) : null });
            }}
          >
            <Button size="sm" variant="ghost" aria-label="Previous puzzle" disabled={!current || current <= 1} onClick={() => current && go({ n: current - 1 })}>
              ←
            </Button>
            <Field label="Puzzle #">
              <TextInput
                inputMode="numeric"
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
                onBlur={() => go({ n: /^\d+$/.test(draft) && Number(draft) > 0 ? Number(draft) : null })}
                placeholder="latest"
                aria-label="Puzzle number"
                style={{ width: 90 }}
              />
            </Field>
            <Button size="sm" variant="ghost" aria-label="Next puzzle" disabled={!current} onClick={() => current && go({ n: current + 1 })}>
              →
            </Button>
            {n !== null ? (
              <Button size="sm" variant="ghost" onClick={() => go({ n: null })}>
                Latest
              </Button>
            ) : null}
          </form>
        ) : null}
        <span className={styles.filtersEnd}>
          {q.data ? <span className="monoLabel" data-testid="board-meta">{boardMeta(q.data)}</span> : null}
        </span>
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <BoardTable board={board} data={q.data?.board === board ? q.data : undefined} busy={q.isFetching} />
      )}
    </>
  );
}

function boardMeta(d: Leaderboard) {
  const scope = d.n !== null ? `puzzle #${d.n}` : d.week ? `week ${d.week}` : d.board === "streak" ? "current streaks" : "all time";
  return `${scope} · top ${Math.min(100, d.entries.length)} of ${fmtInt(d.total)}`;
}

function BoardTable({ board, data, busy }: { board: LeaderboardName; data: Leaderboard | undefined; busy: boolean }) {
  const navigate = useNavigate();
  return (
    <div className={tableStyles.wrap} aria-busy={busy}>
      <table className={tableStyles.table} data-testid="leaderboard">
        <thead>
          <tr>
            <th data-align="right" style={{ width: 70 }}>
              Rank
            </th>
            <th>Player</th>
            <th>User id</th>
            <th data-align="right">{VALUE_HEAD[board]}</th>
          </tr>
        </thead>
        <tbody>
          {!data
            ? Array.from({ length: 10 }, (_, i) => (
                <tr key={i}>
                  {[40, 140, 260, 60].map((w, j) => (
                    <td key={j}>
                      <Skeleton height={12} width={w} />
                    </td>
                  ))}
                </tr>
              ))
            : data.entries.map((e) => (
                <tr key={e.userId} data-clickable data-hidden={e.hidden || undefined} onClick={(ev) => !(ev.target as HTMLElement).closest("a") && navigate(`/players/${e.userId}`)}>
                  <td data-align="right">
                    <span className={styles.rank} data-top={e.rank <= 3 ? e.rank : undefined}>
                      {e.rank}
                    </span>
                  </td>
                  <td>
                    <span className={styles.reportWho}>
                      <span className={e.handle || !e.name.startsWith("Lumen #") ? undefined : tableStyles.dim}>
                        {e.handle ? `@${e.handle}` : e.name.startsWith("Lumen #") ? e.name : `@${e.name}`}
                      </span>
                      {e.moderationHold ? (
                        <Chip tone="danger" title="Held off global boards by moderation">
                          held
                        </Chip>
                      ) : e.hidden ? (
                        <Chip tone="muted" title="Opted out of global leaderboards">
                          hidden
                        </Chip>
                      ) : null}
                    </span>
                  </td>
                  <td>
                    <Link to={`/players/${e.userId}`} className={tableStyles.id}>
                      {e.userId}
                    </Link>
                  </td>
                  <td data-align="right" className="mono">
                    {formatValue(board, e.value)}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
      {data && data.entries.length === 0 ? (
        <EmptyState title="Nobody on this board yet">
          {hasN(board) ? "No submissions for this puzzle (or it hasn't been played yet)." : "The board fills in as players earn it."}
        </EmptyState>
      ) : null}
    </div>
  );
}
